import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AccessGroup, RuleItemRef } from '../../../../models/scan-testing.model';
import { RuleEvent } from './set-rules.component';

interface DraftAccessGroup {
  id: string;
  name: string;
  items: RuleItemRef[];
  nameError: boolean;
}

// Lets users create, rename, and populate reusable Access Groups (named bundles
// of events/plans). Mirrors the gate-groups manager. Emits the full group list.
@Component({
  selector: 'app-sr-access-group-manager',
  templateUrl: './sr-access-group-manager.component.html',
  standalone: false,
  styleUrls: ['./sr-access-group-manager.component.scss']
})
export class SrAccessGroupManagerComponent implements OnChanges {
  @Input() accessGroups: AccessGroup[] = [];
  @Input() events: RuleEvent[] = [];
  @Input() saving = false;
  @Input() error = '';

  @Output() save = new EventEmitter<AccessGroup[]>();
  @Output() cancel = new EventEmitter<void>();

  groups: DraftAccessGroup[] = [];
  activeGroupId: string | null = null;
  expanded = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accessGroups']) { this.rebuild(); }
  }

  private rebuild(): void {
    this.groups = (this.accessGroups || []).map((g) => ({
      id: g.id,
      name: g.name,
      items: (g.items || []).map((ref) => ({ ...ref })),
      nameError: false
    }));
    if (!this.activeGroupId && this.groups.length) {
      this.activeGroupId = this.groups[0].id;
    }
  }

  private newId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  get activeGroup(): DraftAccessGroup | null {
    return this.groups.find((g) => g.id === this.activeGroupId) ?? null;
  }

  addGroup(): void {
    if (this.saving) { return; }
    const group: DraftAccessGroup = { id: this.newId('ag'), name: '', items: [], nameError: false };
    this.groups = [...this.groups, group];
    this.activeGroupId = group.id;
  }

  removeGroup(group: DraftAccessGroup): void {
    if (this.saving) { return; }
    this.groups = this.groups.filter((g) => g.id !== group.id);
    if (this.activeGroupId === group.id) {
      this.activeGroupId = this.groups.length ? this.groups[0].id : null;
    }
  }

  selectGroup(group: DraftAccessGroup): void {
    this.activeGroupId = group.id;
  }

  private itemKey(ref: RuleItemRef): string {
    return ref.kind === 'plan'
      ? `plan:${ref.eventName}:${ref.plan}`
      : `event:${ref.eventName}`;
  }

  eventKey(ev: RuleEvent): string { return `event:${ev.eventName}`; }
  planKey(ev: RuleEvent, plan: string): string { return `plan:${ev.eventName}:${plan}`; }

  isExpanded(ev: RuleEvent): boolean { return this.expanded.has(ev.eventName); }
  toggleExpand(ev: RuleEvent): void {
    if (this.expanded.has(ev.eventName)) { this.expanded.delete(ev.eventName); }
    else { this.expanded.add(ev.eventName); }
  }

  hasItem(group: DraftAccessGroup, key: string): boolean {
    return group.items.some((ref) => this.itemKey(ref) === key);
  }

  isEventInActive(ev: RuleEvent): boolean {
    const g = this.activeGroup;
    return !!g && this.hasItem(g, this.eventKey(ev));
  }

  isPlanInActive(ev: RuleEvent, plan: string): boolean {
    const g = this.activeGroup;
    return !!g && this.hasItem(g, this.planKey(ev, plan));
  }

  toggleEvent(ev: RuleEvent): void {
    const g = this.activeGroup;
    if (!g) { return; }
    const key = this.eventKey(ev);
    if (this.hasItem(g, key)) {
      g.items = g.items.filter((ref) => {
        const k = this.itemKey(ref);
        return k !== key && !ev.plans.some((p) => this.planKey(ev, p) === k);
      });
    } else {
      g.items = [...g.items, { kind: 'event', eventName: ev.eventName }];
    }
  }

  togglePlan(ev: RuleEvent, plan: string): void {
    const g = this.activeGroup;
    if (!g) { return; }
    const key = this.planKey(ev, plan);
    if (this.hasItem(g, key)) {
      g.items = g.items.filter((ref) => this.itemKey(ref) !== key);
    } else {
      g.items = [...g.items, { kind: 'plan', eventName: ev.eventName, plan }];
    }
  }

  private validate(): boolean {
    let ok = true;
    const seen = new Set<string>();
    for (const g of this.groups) {
      const name = g.name.trim();
      const duplicate = !!name && seen.has(name.toLowerCase());
      if (!name || duplicate) {
        g.nameError = true;
        ok = false;
      } else {
        g.nameError = false;
        seen.add(name.toLowerCase());
      }
    }
    return ok;
  }

  onSave(): void {
    if (this.saving || !this.validate()) { return; }
    const out: AccessGroup[] = this.groups
      .filter((g) => g.items.length > 0)
      .map((g) => ({ id: g.id, name: g.name.trim(), items: g.items.map((ref) => ({ ...ref })) }));
    this.save.emit(out);
  }

  onCancel(): void {
    if (this.saving) { return; }
    this.cancel.emit();
  }

  trackGroup(_idx: number, g: DraftAccessGroup): string { return g.id; }
  trackEvent(_idx: number, ev: RuleEvent): string { return ev.eventName; }
  trackPlan(_idx: number, plan: string): string { return plan; }
}
